import {
  casaDellaComunitaVimercate,
  casaDellaComunitaBellusco,
  casaDellaComunitaBesanaBrianza,
  casaDellaComunitaBrugherio,
  casaDellaComunitaCesanoMaderno,
  casaDellaComunitaDesio,
  casaDellaComunitaGiussano,
  casaDellaComunitaLimbiate,
  casaDellaComunitaLentateSulSeveso,
  casaDellaComunitaLissone,
  casaDellaComunitaMonza,
  casaDellaComunitaMonzaViaBorgazzi,
  casaDellaComunitaMonzaViaLucaDellaRobbia,
  casaDellaComunitaAgrate,
  casaDellaComunitaSeregno,
  casaDellaComunitaMacherio,
  casaDellaComunitaNovaMilanese,
  type CasaDellaComunita,
} from './CdC'
import type { AmbitoId } from './ambitiStats'
import { AMBITI_STATS } from './ambitiStats'
import { PNRR_INTERVENTI } from './pnrr'

export interface CdCWithAmbito {
  casa: CasaDellaComunita
  ambitoId: AmbitoId
  comuneNormalizzato: string
  pnrrInvestimento: number
}

const PNRR_CDC_SUBMISURA = 'I1.1 - Case della Comunità e presa in carico della persona'

/** All CdC instances */
const ALL_CDC: CasaDellaComunita[] = [
  casaDellaComunitaVimercate,
  casaDellaComunitaBellusco,
  casaDellaComunitaBesanaBrianza,
  casaDellaComunitaBrugherio,
  casaDellaComunitaCesanoMaderno,
  casaDellaComunitaDesio,
  casaDellaComunitaGiussano,
  casaDellaComunitaLimbiate,
  casaDellaComunitaLentateSulSeveso,
  casaDellaComunitaLissone,
  casaDellaComunitaMonza,
  casaDellaComunitaMonzaViaBorgazzi,
  casaDellaComunitaMonzaViaLucaDellaRobbia,
  casaDellaComunitaAgrate,
  casaDellaComunitaSeregno,
  casaDellaComunitaMacherio,
  casaDellaComunitaNovaMilanese,
]

function normalizeComune(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’`]/g, '')
    .replace(/\bin\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function extractComuneFromCitta(citta: string): string {
  // "Vimercate (MB)" → "vimercate"
  return citta.replace(/\s*\(.*\)\s*$/, '').trim().toLowerCase()
}

function extractComuneFromNome(nome: string): string {
  const match = nome.match(/casa della comunità di\s+(.+?)(?:\s*\(|$)/i)
  return (match?.[1] ?? '').trim().toLowerCase()
}

function extractComune(casa: CasaDellaComunita): string {
  const fromCitta = extractComuneFromCitta(casa.citta)
  if (fromCitta) return fromCitta
  return extractComuneFromNome(casa.nome)
}

function findAmbito(comune: string): AmbitoId | null {
  if (!comune) return null
  const comuneNorm = normalizeComune(comune)

  for (const ambito of AMBITI_STATS) {
    if (ambito.comuni.some(c => normalizeComune(c) === comuneNorm)) {
      return ambito.id
    }
    if (ambito.comuni.some(c => {
      const comuneAmbitoNorm = normalizeComune(c)
      return comuneAmbitoNorm.includes(comuneNorm) || comuneNorm.includes(comuneAmbitoNorm)
    })) {
      return ambito.id
    }
  }
  return null
}

function findAmbitoFromSezioneTerritoriale(value: string): AmbitoId | null {
  const normalized = normalizeComune(value)
  if (normalized.includes('carate')) return 'carate'
  if (normalized.includes('desio')) return 'desio'
  if (normalized.includes('vimercate')) return 'vimercate'
  if (normalized.includes('seregno')) return 'seregno'
  if (normalized.includes('monza')) return 'monza'
  return null
}

function parsePnrrRisorse(value: string): number {
  const parsed = Number(value.replace(/[^\d.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

function getPnrrInvestimentoForComune(comuneNormalizzato: string): number {
  return PNRR_INTERVENTI
    .filter(intervento => (
      intervento.codiceTitoloSubmisura === PNRR_CDC_SUBMISURA
      && normalizeComune(intervento.luogo) === comuneNormalizzato
    ))
    .reduce((acc, intervento) => acc + parsePnrrRisorse(intervento.risorsePreviste), 0)
}

export const CDC_WITH_AMBITO: CdCWithAmbito[] = ALL_CDC.map(casa => {
  const comune = extractComune(casa)
  const comuneNormalizzato = normalizeComune(comune)
  const ambitoId = findAmbito(comune) ?? findAmbitoFromSezioneTerritoriale(casa.sezioneTerritoriale ?? '')
  return {
    casa,
    ambitoId: ambitoId ?? 'monza', // fallback
    comuneNormalizzato,
    pnrrInvestimento: getPnrrInvestimentoForComune(comuneNormalizzato),
  }
})

/** Generate a URL-safe slug from CdC name */
export function cdcSlug(casa: CasaDellaComunita): string {
  return casa.nome
    .toLowerCase()
    .replace(/casa della comunità di\s*/i, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
}

/** Find a CdC by its slug */
export function findCdcBySlug(slug: string): CdCWithAmbito | undefined {
  return CDC_WITH_AMBITO.find(c => cdcSlug(c.casa) === slug)
}

/** Get all unique service names across given CdC list */
export function getUniqueServices(caseList: CdCWithAmbito[]): string[] {
  const serviceSet = new Set<string>()
  for (const { casa } of caseList) {
    for (const sezione of casa.sezioni) {
      for (const servizio of sezione.servizi) {
        serviceSet.add(servizio.nome.trim())
      }
    }
  }
  return Array.from(serviceSet).sort()
}
